/**
 * How long Do Not Disturb lasts, and how to say when it ends.
 *
 * The choices match what people already know from Quo: 30 minutes, 1 hour,
 * 8 hours, until tomorrow morning, or until they turn it off. "Tomorrow" ends
 * at 8:00 AM — the label says so, because a bare "Tomorrow" leaves people
 * guessing whether that means midnight.
 *
 * Every phrase here names what is actually being silenced — RINGING — and
 * never leans on a bare "it". A menu that said "Turn off · Until 8:12 PM" was
 * read by customers as "turn ringing off until 8:12 PM", the exact opposite of
 * what the row did, so the same vocabulary is used everywhere: calls are
 * silenced, and ringing is turned back on.
 *
 * Wall-clock times ("tomorrow at 8", "until 3:30 PM") are worked out in the
 * person's schedule display zone, the same zone every other time in the app is
 * shown in — never whatever the laptop's clock happens to be set to. The
 * calling service only ever sees the resulting instant.
 */

import {
  dateKeyInTimeZone,
  dateStringToDate,
  dateToDateString,
  formatDateTimeInTimeZone,
  zonedDateTimeToUtc,
} from '@/lib/date-values';

export type DoNotDisturbChoiceId = '30m' | '1h' | '8h' | 'tomorrow' | 'manual';

export interface DoNotDisturbChoice {
  id: DoNotDisturbChoiceId;
  /** What the row shows, kept short enough to sit on one line. */
  label: string;
  /**
   * The whole sentence for screen readers, e.g. "Silence calls on this
   * computer for 1 hour" — the visible label alone ("For 1 hour") says nothing
   * about what it does once it is read out of the menu's context.
   */
  announce: string;
  /** When DND should end if chosen at `now`; null for the open-ended choice. */
  until: (now: Date, timeZone?: string | null) => Date | null;
}

const MINUTE_MS = 60 * 1000;
const TOMORROW_ENDS_AT = '08:00';

const after = (minutes: number) => (now: Date) => new Date(now.getTime() + minutes * MINUTE_MS);

/** The calendar day after `now`, as seen in `timeZone`, as a YYYY-MM-DD key. */
function nextDayKey(now: Date, timeZone?: string | null): string {
  const today = dateStringToDate(dateKeyInTimeZone(now.toISOString(), timeZone));
  if (!today) return '';
  // Date-only arithmetic: only the calendar fields are read back, so a
  // daylight-saving change overnight cannot shift the day.
  today.setDate(today.getDate() + 1);
  return dateToDateString(today);
}

const announce = (span: string) => `Silence calls on this computer ${span}`;

export const DO_NOT_DISTURB_CHOICES: readonly DoNotDisturbChoice[] = [
  { id: '30m', label: 'For 30 minutes', announce: announce('for 30 minutes'), until: after(30) },
  { id: '1h', label: 'For 1 hour', announce: announce('for 1 hour'), until: after(60) },
  { id: '8h', label: 'For 8 hours', announce: announce('for 8 hours'), until: after(8 * 60) },
  {
    id: 'tomorrow',
    label: 'Until tomorrow at 8:00 AM',
    announce: announce('until tomorrow at 8:00 AM'),
    until: (now, timeZone) =>
      zonedDateTimeToUtc(dateStringToDate(nextDayKey(now, timeZone)), TOMORROW_ENDS_AT, timeZone) ??
      null,
  },
  {
    id: 'manual',
    label: 'Until I turn ringing back on',
    announce: announce('until I turn ringing back on'),
    until: () => null,
  },
];

/**
 * What to call a device in the menu: the name it registered with, else a plain
 * word for the kind of device. "This computer" for the browser, because that is
 * what the person is looking at.
 */
export function deviceLabel(device: {
  deviceName?: string | null;
  platform?: string | null;
}): string {
  const platform = String(device?.platform || '').toLowerCase();
  if (platform === 'web') return 'This computer';
  const named = String(device?.deviceName || '').trim();
  if (named && named.toLowerCase() !== 'web') return named;
  if (platform === 'ios') return 'iPhone';
  if (platform === 'android') return 'Android phone';
  if (platform === 'landline') return 'Desk phone';
  return 'Device';
}

/** Whether a saved setting is still silencing calls at `now`. */
export function isDoNotDisturbInForce(
  state: { enabled: boolean; until: string | null } | undefined,
  now: number = Date.now(),
): boolean {
  if (!state?.enabled) return false;
  if (!state.until) return true;
  const ends = Date.parse(state.until);
  return Number.isNaN(ends) ? true : ends > now;
}

/**
 * How long the silence lasts, as a phrase that slots into a sentence:
 * "until 3:30 PM", "until tomorrow at 8:00 AM", "until Thursday at 8:00 AM",
 * or "until you turn ringing back on" — in `timeZone`.
 *
 * Lower case and mid-sentence on purpose. Callers write the sentence around it
 * ("Calls are silenced until 3:30 PM"), which is what stops a bare end time
 * from being read as an instruction.
 */
export function describeDoNotDisturbEnd(
  until: string | null,
  timeZone?: string | null,
  now: Date = new Date(),
): string {
  if (!until || Number.isNaN(Date.parse(until))) return 'until you turn ringing back on';

  const time = formatDateTimeInTimeZone(until, timeZone, { hour: 'numeric', minute: '2-digit' });
  const endsKey = dateKeyInTimeZone(until, timeZone);
  if (endsKey === dateKeyInTimeZone(now.toISOString(), timeZone)) return `until ${time}`;
  if (endsKey === nextDayKey(now, timeZone)) return `until tomorrow at ${time}`;

  const day = formatDateTimeInTimeZone(until, timeZone, { weekday: 'long' });
  return `until ${day} at ${time}`;
}
